/**
 * `integrations-mgmt-api` reads, plus the one write this app makes: PROMOTE.
 *
 * The authoring lifecycle — register → presigned upload → build → promote —
 * lives in the code editor, a separate app. Everything up to `build` needs an
 * editor; `promote` does not. It is a one-field decision ("which built version
 * is live?") whose consequence is tenancy-wide, and it had no surface in either
 * app: the editor builds, and nothing anywhere called
 * `POST /v0/integrations/{id}/promote`. So `live_version_id` stayed null on
 * every integration a tenant had ever registered, `isRunnableIntegration` was
 * false for all of them, and the pipeline builder's picker rendered a list where
 * every row was disabled with "No version published yet" — code that had built
 * successfully, described as unpublished, with no way to publish it.
 *
 * Both list routes are PAGED (`{items, cursor}` — note `cursor`, not
 * trigger-mgmt-api's `next_cursor`). Reading them as bare arrays is the bug
 * `integrationsFromResponse` exists to stop repeating.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import {
  integrationsFromResponse,
  type PipelineIntegration,
} from "@authe/fe-commons/pipeline";

const INTEGRATIONS_PATH = "/integrations";
const VERSIONS_PATH = "/integration-versions";

/**
 * Where a build got to. Mirrors `commons.interfaces.models.integration_version.
 * VersionStatus`; only `built` may be promoted.
 */
export type IntegrationVersionStatus = "pending" | "building" | "built" | "failed";

/** One immutable build record, as `GET /v0/versions?integration_id=` returns it. */
export interface IntegrationVersionRow {
  id: string;
  integration_id: string;
  runtime_id?: string;
  status?: IntegrationVersionStatus;
  created_at?: number;
  build_started_at?: number | null;
  build_finished_at?: number | null;
  error?: string | null;
  source_digest?: string | null;
  function_name?: string | null;
  function_version?: string | null;
}

/**
 * Whether this build may be made live.
 *
 * Both halves, because the service enforces both: `IntegrationVersion.
 * is_promotable` is `status in PROMOTABLE_STATUSES and bool(function_name)`. A
 * row marked `built` with no published function is a build that produced an
 * artifact and never got a Lambda — offering it would be offering a 4xx.
 */
export function isPromotableVersion(version: IntegrationVersionRow): boolean {
  return version.status === "built" && Boolean(version.function_name);
}

/** Why this version cannot be promoted, or `null` when it can. */
export function unpromotableReason(version: IntegrationVersionRow): string | null {
  switch (version.status) {
    case "built":
      return version.function_name ? null : "Built, but no function was published";
    case "failed":
      return version.error ? `Build failed: ${version.error}` : "Build failed";
    case "building":
      return "Still building";
    case "pending":
    default:
      return "Not built yet";
  }
}

export interface IntegrationsSource {
  listIntegrations(signal?: AbortSignal): Promise<PipelineIntegration[]>;
  listVersions(integrationId: string, signal?: AbortSignal): Promise<IntegrationVersionRow[]>;
  promote(integrationId: string, versionId: string): Promise<PipelineIntegration>;
}

function pageItems<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  if (typeof body !== "object" || body === null) return [];
  const items = (body as { items?: unknown }).items;
  return Array.isArray(items) ? (items as T[]) : [];
}

export function createIntegrationsSource(client: ApiClient): IntegrationsSource {
  return {
    async listIntegrations(signal) {
      return integrationsFromResponse(
        unwrapData(await client.get<unknown>(INTEGRATIONS_PATH, { signal })),
      );
    },

    async listVersions(integrationId, signal) {
      return pageItems<IntegrationVersionRow>(
        unwrapData(
          await client.get<unknown>(VERSIONS_PATH, {
            query: { integration_id: integrationId },
            signal,
          }),
        ),
      );
    },

    async promote(integrationId, versionId) {
      const row = unwrapData(
        await client.post<PipelineIntegration>(
          `${INTEGRATIONS_PATH}/${encodeURIComponent(integrationId)}/promote`,
          { version_id: versionId },
        ),
      );
      // Both lists are cached GETs and both change: the integration now has a
      // different `live_version_id`, and the version list is what a second
      // promote would be chosen from. `invalidateMatching` rather than the
      // exact-URL form because the versions read carries a query string.
      client.invalidateMatching(INTEGRATIONS_PATH);
      client.invalidateMatching(VERSIONS_PATH);
      return row;
    },
  };
}
